import * as React from 'react';
import type { Control, FieldValues, Validate } from 'react-hook-form';
import { Controller } from 'react-hook-form';
import Help from '@mui/icons-material/Help';
import { FormControl } from '@mui/material';

import { Container, ContainerItem, Error, Input, InputLabel, Tooltip } from 'ui';
import { errorMessages } from 'util/forms.util';

interface ValidationRule<T> {
  message: string;
  value: T;
}

interface Props {
  label: string;
  name: string;
  control?: Control;
  placeholder?: string;
  defaultValue?: string;
  readOnly?: boolean;
  viewOnly?: boolean;
  focus?: boolean;
  rules?: {
    required?: ValidationRule<boolean>;
    maxLength?: ValidationRule<number>;
    minLength?: ValidationRule<number>;
    max?: ValidationRule<number>;
    min?: ValidationRule<number>;
    pattern?: ValidationRule<RegExp>;
    validate?:
      | Validate<string | number, FieldValues>
      | Record<string, Validate<string | number, FieldValues>>;
  };
  style?: React.CSSProperties;
  dataCy?: string;
  minRows?: number;
  maxRows?: number;
  onBlur?: () => void;
  tooltip?: string | React.ReactElement;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  background?: 'white' | 'gray' | 'lightGray';
}

export const InputTextArea: React.FC<Props> = (props) => {
  return (
    <FormControl fullWidth data-cy={props.dataCy}>
      <Container padding={0} align="center">
        <ContainerItem padding={0} align="center" flex margin="0 0 6px 0">
          <InputLabel
            dataCy={'input-label'}
            label={props.label}
            name={props.name}
            required={props.viewOnly ? false : props.rules?.required?.value}
            readOnly={props.readOnly}
            rules={props.rules}
            viewOnly={props.viewOnly}
          />
        </ContainerItem>

        {props.tooltip ? (
          <ContainerItem padding={0} align="center" flex margin="-3px 0 4px 2px">
            <Tooltip variant="text" title={props.tooltip} placement="right">
              <Help color="primary" />
            </Tooltip>
          </ContainerItem>
        ) : null}
      </Container>

      <Controller
        control={props.control}
        name={props.name}
        defaultValue={props.defaultValue}
        rules={props.rules}
        render={({ field: { onBlur, onChange, value }, fieldState: { error } }) => (
          <>
            <Input
              multiline
              readOnly={props.readOnly}
              name={props.name}
              dataCy={'input-text'}
              onBlur={() => {
                if (props.readOnly) {
                  return;
                }

                if (props.onBlur) {
                  props.onBlur();
                }

                onBlur();
              }}
              onChange={(e) => {
                if (props.onChange) {
                  props.onChange(e);
                }

                onChange(e.target.value);
              }}
              value={(props.readOnly || props.viewOnly) && (value == null || value === '') ? '--' : value}
              error={error}
              background={props.background || 'white'}
              placeholder={props.placeholder}
              viewOnly={props.viewOnly}
              focus={props.focus}
              minRows={props.minRows ? props.minRows : 3}
              maxRows={props.maxRows}
            />
            <Error
              content={error ? `* ${props.label} ${error.message || errorMessages()[error.type]}` : ''}
            />
          </>
        )}
      />
    </FormControl>
  );
};
